import React, { useEffect, useMemo, useState } from "react";
import {
  MDBBadge,
  MDBBtn,
  MDBCard,
  MDBCardBody,
  MDBCardHeader,
  MDBCol,
  MDBContainer,
  MDBIcon,
  MDBInput,
  MDBListGroup,
  MDBListGroupItem,
  MDBRow,
  MDBSpinner,
  MDBTextArea,
} from "mdb-react-ui-kit";
import { toast } from "Projects/CC_cubi_clan_project/GlobalComponents/ToastHost/ToastHost";
import {
  addIncidentMessage,
  assignIncident,
  getIncidentDetail,
  getIncidentMyRole,
  getManageIncidents,
  getMyIncidents,
  resolveIncidentByUser,
  updateIncidentStatus,
} from "Projects/CC_cubi_clan_project/Utils/IncidentService";
import { mfmPosterUrl, mfmThumbUrl } from "Projects/CC_cubi_clan_project/Utils/MfmService";
import { MfmImageUploader } from "Projects/CC_cubi_clan_project/GlobalComponents/MfmImageUploader/MfmImageUploader";
import IncidentDetailModal from "Projects/CC_cubi_clan_project/GlobalComponents/IncidentDetailModal/IncidentDetailModal";
import IncidentKanbanBoard from "Projects/CC_cubi_clan_project/GlobalComponents/IncidentKanbanBoard/IncidentKanbanBoard";
import IncidentCreateModal from "Projects/CC_cubi_clan_project/GlobalComponents/IncidentCreateModal/IncidentCreateModal";
import "./IncidentWorkspace.css";

const STATUS_LABEL = {
  OPEN: "Abierta",
  IN_PROGRESS: "En curso",
  WAITING_USER: "Esperando usuario",
  RESOLVED: "Resuelta",
  CLOSED: "Cerrada",
};

const PRIORITY_COLOR = {
  LOW: "success",
  MEDIUM: "warning",
  HIGH: "danger",
  URGENT: "dark",
};

function parseJsonArray(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function IncidentCard({ item, selectedId, onClick }) {
  return (
    <MDBListGroupItem
      action
      active={selectedId === item.idIncidencia}
      onClick={() => onClick(item.idIncidencia)}
      className="inci-item"
    >
      <div className="d-flex justify-content-between align-items-start gap-2">
        <div>
          <div className="fw-bold">#{item.idIncidencia} · {item.titulo}</div>
          <small className="text-muted">{item.appCode} · {item.creadorApodo || item.creadorNombre || "Usuario"}</small>
        </div>
        <MDBBadge color={PRIORITY_COLOR[item.prioridad] || "secondary"}>{item.prioridad || "MEDIUM"}</MDBBadge>
      </div>
      <div className="mt-1 d-flex gap-2 align-items-center">
        <MDBBadge color="info">{STATUS_LABEL[item.estado] || item.estado}</MDBBadge>
        {item.asignadoApodo && <small className="text-muted">Asignada a: {item.asignadoApodo}</small>}
      </div>
    </MDBListGroupItem>
  );
}

export default function IncidentWorkspace({
  appCode = "GEN",
  title = "Incidencias",
  subtitle,
  forceManagerView = false,
  initialTab = "mine",
  lockTab = false,
  enableManage = true,
}) {
  const defaultManageFilters = {
    appCode: appCode !== "GEN" ? appCode : "",
    status: "",
    priority: "",
    assignedUserId: "",
    creatorUserId: "",
    search: "",
  };

  const [loading, setLoading] = useState(false);
  const [myRole, setMyRole] = useState({ isManager: false, isBackdoor: false });
  const normalizedInitialTab = initialTab === "manage" ? "manage" : "mine";
  const [activeTab, setActiveTab] = useState(normalizedInitialTab);
  const [mine, setMine] = useState([]);
  const [manage, setManage] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const [newMessage, setNewMessage] = useState("");
  const [internalNote, setInternalNote] = useState(false);
  const [messageAttachments, setMessageAttachments] = useState([]);
  const [statusDraft, setStatusDraft] = useState("OPEN");
  const [assignedDraft, setAssignedDraft] = useState("");
  const [manageFilters, setManageFilters] = useState(defaultManageFilters);

  // Vista Kanban
  const [viewMode, setViewMode] = useState("list"); // 'list' | 'kanban'
  const [detailModalId, setDetailModalId] = useState(null);

  const isManager = myRole.isManager || forceManagerView;
  const managerEnabled = isManager && enableManage;
  const effectiveTab = lockTab
    ? (normalizedInitialTab === "manage" && managerEnabled ? "manage" : "mine")
    : (activeTab === "manage" && managerEnabled ? "manage" : "mine");
  const listToShow = effectiveTab === "manage" ? manage : mine;

  const selectedSummary = useMemo(() => {
    return listToShow.find((it) => it.idIncidencia === selectedId) || null;
  }, [listToShow, selectedId]);

  useEffect(() => {
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeTab === "manage" && !managerEnabled) {
      setActiveTab("mine");
    }
  }, [activeTab, managerEnabled]);

  useEffect(() => {
    setManageFilters((prev) => ({
      ...prev,
      appCode: appCode !== "GEN" ? appCode : prev.appCode,
    }));
  }, [appCode]);

  const normalizeManageFilterNumber = (value) => {
    if (value == null || String(value).trim() === "") return undefined;
    const parsed = Number(String(value).trim());
    return Number.isFinite(parsed) ? parsed : undefined;
  };

  const loadManageIncidents = async (filters = manageFilters) => {
    return getManageIncidents({
      appCode: filters.appCode || undefined,
      status: filters.status || undefined,
      priority: filters.priority || undefined,
      assignedUserId: normalizeManageFilterNumber(filters.assignedUserId),
      creatorUserId: normalizeManageFilterNumber(filters.creatorUserId),
      search: filters.search || undefined,
    });
  };

  const init = async () => {
    setLoading(true);
    try {
      const role = await getIncidentMyRole();
      setMyRole(role || { isManager: false, isBackdoor: false });

      const mineRows = await getMyIncidents({ appCode: appCode !== "GEN" ? appCode : undefined });
      setMine(mineRows);

      if ((role?.isManager || forceManagerView) && enableManage && appCode) {
        const manageRows = await loadManageIncidents({
          ...manageFilters,
          appCode: appCode !== "GEN" ? appCode : manageFilters.appCode,
        });
        setManage(manageRows);
      }
    } catch {
      toast.error("No se pudieron cargar las incidencias");
    } finally {
      setLoading(false);
    }
  };

  const refreshLists = async () => {
    const mineRows = await getMyIncidents({ appCode: appCode !== "GEN" ? appCode : undefined });
    setMine(mineRows);
    if (managerEnabled) {
      const manageRows = await loadManageIncidents();
      setManage(manageRows);
    }
  };

  const applyManageFilters = async () => {
    if (!managerEnabled) return;
    setLoading(true);
    try {
      const manageRows = await loadManageIncidents();
      setManage(manageRows);
      if (selectedId && !manageRows.some((it) => it.idIncidencia === selectedId)) {
        setSelectedId(null);
        setDetail(null);
      }
    } catch {
      toast.error("No se pudieron aplicar los filtros de incidencias");
    } finally {
      setLoading(false);
    }
  };

  const resetManageFilters = async () => {
    const reset = {
      appCode: appCode !== "GEN" ? appCode : "",
      status: "",
      priority: "",
      assignedUserId: "",
      creatorUserId: "",
      search: "",
    };
    setManageFilters(reset);
    if (!managerEnabled) return;
    setLoading(true);
    try {
      const manageRows = await loadManageIncidents(reset);
      setManage(manageRows);
    } catch {
      toast.error("No se pudieron resetear los filtros de incidencias");
    } finally {
      setLoading(false);
    }
  };

  const onMediaPreviewError = (event) => {
    const img = event.currentTarget;
    const uuid = img?.dataset?.uuid;
    if (!uuid) return;

    if (!img.dataset.triedPoster) {
      img.dataset.triedPoster = "1";
      img.src = mfmPosterUrl(uuid);
      return;
    }

    img.onerror = null;
  };

  const onSelect = async (id) => {
    setSelectedId(id);
    setLoading(true);
    try {
      const data = await getIncidentDetail(id);
      setDetail(data);
      if (data?.estado) setStatusDraft(data.estado);
      setAssignedDraft(data?.idUsuarioAsignado != null ? String(data.idUsuarioAsignado) : "");
    } catch {
      toast.error("No se pudo cargar el detalle de la incidencia");
      setDetail(null);
    } finally {
      setLoading(false);
    }
  };

  const addAttachment = (result) => {
    const uuid = result?.uuid;
    if (!uuid) return;
    setMessageAttachments((prev) => (prev.includes(uuid) ? prev : [...prev, uuid]));
  };

  const sendMessage = async () => {
    if (!selectedId || (!newMessage.trim() && messageAttachments.length === 0)) return;
    setLoading(true);
    try {
      const payload = {
        content: newMessage.trim(),
        internalNote,
        messageType: internalNote ? "NOTE" : "MESSAGE",
        attachmentsJson: messageAttachments.length ? JSON.stringify(messageAttachments) : null,
      };
      const res = await addIncidentMessage(selectedId, payload);
      if (res.ok && res.json?.STATE === "SUCCESS") {
        setNewMessage("");
        setInternalNote(false);
        setMessageAttachments([]);
        await onSelect(selectedId);
        await refreshLists();
      } else {
        toast.error(res.json?.MESSAGE || "No se pudo enviar el mensaje");
      }
    } finally {
      setLoading(false);
    }
  };

  const markResolved = async () => {
    if (!selectedId) return;
    setLoading(true);
    try {
      const res = await resolveIncidentByUser(selectedId);
      if (res.ok && res.json?.STATE === "SUCCESS") {
        await onSelect(selectedId);
        await refreshLists();
      } else {
        toast.error(res.json?.MESSAGE || "No se pudo marcar como resuelta");
      }
    } finally {
      setLoading(false);
    }
  };

  const saveManagerChanges = async () => {
    if (!selectedId || !isManager) return;
    setLoading(true);
    try {
      const updates = [];
      if (statusDraft && statusDraft !== detail?.estado) {
        updates.push(updateIncidentStatus(selectedId, statusDraft));
      }

      const parsedAssigned = assignedDraft.trim() === "" ? null : Number(assignedDraft);
      const currentAssigned = detail?.idUsuarioAsignado == null ? null : Number(detail.idUsuarioAsignado);
      if (parsedAssigned !== currentAssigned && (parsedAssigned == null || Number.isFinite(parsedAssigned))) {
        updates.push(assignIncident(selectedId, parsedAssigned));
      }

      if (!updates.length) {
        toast.info("No hay cambios de gestión pendientes");
        return;
      }

      const results = await Promise.all(updates);
      const failed = results.find((r) => !(r.ok && r.json?.STATE === "SUCCESS"));
      if (failed) {
        toast.error(failed.json?.MESSAGE || "No se pudieron guardar los cambios");
      } else {
        await onSelect(selectedId);
        await refreshLists();
      }
    } finally {
      setLoading(false);
    }
  };

  const mediaFromDescription = parseJsonArray(detail?.descriptionMediaJson);

  // ── Kanban handlers ──

  const handleKanbanStatusChange = async (id, newStatus) => {
    setLoading(true);
    try {
      const res = await updateIncidentStatus(id, newStatus);
      if (res.ok && res.json?.STATE === "SUCCESS") {
        await refreshLists();
      } else {
        toast.error(res.json?.MESSAGE || "No se pudo cambiar el estado");
      }
    } catch {
      toast.error("No se pudo cambiar el estado de la incidencia");
    } finally {
      setLoading(false);
    }
  };

  const openDetailModal = (id) => setDetailModalId(id);
  const closeDetailModal = () => setDetailModalId(null);

  return (
    <MDBContainer fluid className="inci-workspace py-3">
      <IncidentCreateModal
        show={showCreate}
        onClose={() => setShowCreate(false)}
        appCode={appCode}
        onCreated={async () => {
          await refreshLists();
        }}
      />

      <IncidentDetailModal
        show={detailModalId != null}
        onClose={closeDetailModal}
        incidentId={detailModalId}
        isManager={isManager}
        onUpdated={async () => { await refreshLists(); }}
      />

      <MDBCard className="mb-3">
        <MDBCardHeader className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div>
            <h4 className="mb-1">{title}</h4>
            {subtitle && <div className="text-muted small">{subtitle}</div>}
          </div>
          <div className="d-flex gap-2 align-items-center">
            {myRole.isBackdoor && <MDBBadge color="dark">Backdoor Copilot activa</MDBBadge>}
            {managerEnabled && (
              <div className="inci-view-toggle">
                <MDBBtn
                  size="sm"
                  color={viewMode === "list" ? "primary" : "light"}
                  onClick={() => setViewMode("list")}
                  title="Vista lista"
                >
                  <MDBIcon fas icon="list" />
                </MDBBtn>
                <MDBBtn
                  size="sm"
                  color={viewMode === "kanban" ? "primary" : "light"}
                  onClick={() => setViewMode("kanban")}
                  title="Vista Kanban"
                >
                  <MDBIcon fas icon="columns" />
                </MDBBtn>
              </div>
            )}
            <MDBBtn color="danger" onClick={() => setShowCreate(true)}>
              <MDBIcon fas icon="plus" className="me-2" />Nueva incidencia
            </MDBBtn>
          </div>
        </MDBCardHeader>
      </MDBCard>

      {/* ── Vista Kanban ── */}
      {viewMode === "kanban" && managerEnabled && (
        <IncidentKanbanBoard
          incidents={manage}
          isManager={isManager}
          loadingStatus={loading}
          onStatusChange={handleKanbanStatusChange}
          onCardClick={openDetailModal}
        />
      )}

      {/* ── Vista Lista ── */}
      {(viewMode === "list" || !managerEnabled) && (
      <MDBRow>
        <MDBCol md="12">
          <MDBCard>
            <MDBCardHeader className="d-flex gap-2">
              {!lockTab && (
                <MDBBtn size="sm" color={activeTab === "mine" ? "primary" : "light"} onClick={() => setActiveTab("mine")}>Mis incidencias</MDBBtn>
              )}
              {!lockTab && managerEnabled && (
                <MDBBtn size="sm" color={activeTab === "manage" ? "primary" : "light"} onClick={() => setActiveTab("manage")}>Gestión</MDBBtn>
              )}
            </MDBCardHeader>
            <MDBCardBody className="inci-list-col">
              {effectiveTab === "manage" && managerEnabled && (
                <div className="inci-manager-filters mb-3">
                  <div className="d-flex flex-wrap gap-2">
                    <MDBInput
                      label="Buscar"
                      size="sm"
                      value={manageFilters.search}
                      onChange={(e) => setManageFilters((prev) => ({ ...prev, search: e.target.value }))}
                    />
                    <select
                      className="form-select form-select-sm"
                      value={manageFilters.status}
                      onChange={(e) => setManageFilters((prev) => ({ ...prev, status: e.target.value }))}
                    >
                      <option value="">Estado: todos</option>
                      <option value="OPEN">OPEN</option>
                      <option value="IN_PROGRESS">IN_PROGRESS</option>
                      <option value="WAITING_USER">WAITING_USER</option>
                      <option value="RESOLVED">RESOLVED</option>
                      <option value="CLOSED">CLOSED</option>
                    </select>
                    <select
                      className="form-select form-select-sm"
                      value={manageFilters.priority}
                      onChange={(e) => setManageFilters((prev) => ({ ...prev, priority: e.target.value }))}
                    >
                      <option value="">Criticidad: todas</option>
                      <option value="LOW">LOW</option>
                      <option value="MEDIUM">MEDIUM</option>
                      <option value="HIGH">HIGH</option>
                      <option value="URGENT">URGENT</option>
                    </select>
                    <MDBInput
                      label="ID usuario incidencia"
                      size="sm"
                      value={manageFilters.creatorUserId}
                      onChange={(e) => setManageFilters((prev) => ({ ...prev, creatorUserId: e.target.value }))}
                    />
                    <MDBInput
                      label="ID usuario asignado"
                      size="sm"
                      value={manageFilters.assignedUserId}
                      onChange={(e) => setManageFilters((prev) => ({ ...prev, assignedUserId: e.target.value }))}
                    />
                    {appCode === "GEN" && (
                      <MDBInput
                        label="Aplicación"
                        size="sm"
                        value={manageFilters.appCode}
                        onChange={(e) => setManageFilters((prev) => ({ ...prev, appCode: e.target.value }))}
                      />
                    )}
                  </div>
                  <div className="d-flex gap-2 mt-2">
                    <MDBBtn size="sm" color="primary" onClick={applyManageFilters}>Filtrar</MDBBtn>
                    <MDBBtn size="sm" color="light" onClick={resetManageFilters}>Limpiar</MDBBtn>
                  </div>
                </div>
              )}
              {listToShow.map((item) => (
                <MDBListGroup key={`grp-${item.idIncidencia}`} className="mb-2">
                  <IncidentCard item={item} selectedId={detailModalId} onClick={openDetailModal} />
                </MDBListGroup>
              ))}
              {!listToShow.length && <div className="text-muted">No hay incidencias en este panel.</div>}
            </MDBCardBody>
          </MDBCard>
        </MDBCol>
      </MDBRow>
      )} {/* fin vista Lista */}

      {loading && (
        <div className="inci-loading-overlay">
          <MDBSpinner />
        </div>
      )}
    </MDBContainer>
  );
}
